// src/App.tsx
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  useMemo,
  createContext,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from './app/hooks';
import LandingPage from './features/landing/LandingPage';
import { ContentWrapper } from './App.styled';
import { logout, setAuthSession, validateToken } from './features/auth/authSlice';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useMaintenanceStatus } from './features/landing/maintenance';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import axios from './axios';
import { applySeo } from './seo';
import AutopilotBetaBadge from './features/autopilot/AutopilotBetaBadge';
import type {
  AutopilotBootstrapResponse,
  AutopilotSessionReportResponse,
} from './features/autopilot/types';

const LoginForm = lazy(() => import('./features/auth/LoginForm'));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage'));
const OAuthCallbackPage = lazy(() => import('./features/auth/OAuthCallbackPage'));
const PrivacyPage = lazy(() => import('./features/legal/PrivacyPage'));
const PublicSignalsPage = lazy(() => import('./features/publicSignals/PublicSignalsPage'));
const BinanceAccountForm = lazy(() => import('./features/binance/BinanceAccountForm'));
const BotForm = lazy(() => import('./features/bots/BotForm'));
const BotList = lazy(() => import('./features/bots/BotList'));
const NotificationBanner = lazy(() => import('./features/payments/components/NotificationBanner'));
const UserPaymentDashboard = lazy(() => import('./features/payments/pages/UserPaymentDashboard'));
const AdminPaymentDashboard = lazy(() => import('./features/payments/pages/AdminPaymentDashboard'));
const GlobalStat = lazy(() => import('./features/globalStat/globalStat'));
const Navbar = lazy(() => import('./features/Navbar/Navbar'));
const CustomStrategiesPage = lazy(() => import('./features/customStrategies/CustomStrategiesPage'));
const AnalyticsPage = lazy(() => import('./features/analytics/AnalyticsPage'));

// Контекст для переключения темы
export const ThemeContext = createContext<{
  isDark: boolean;
  toggleTheme: () => void;
}>({
  isDark: false,
  toggleTheme: () => {},
});

// Утилита: получить роль из JWT
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useRoleFromToken = (token: string | null) => {
  return useMemo<'admin' | 'user' | 'guest' | null>(() => {
    try {
      if (!token) return null;
      const [, payloadB64] = token.split('.');
      if (!payloadB64) return null;
      const json = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      return json?.role ?? null;
    } catch {
      return null;
    }
  }, [token]);
};

// Компонента, оборачивающая приложение с ThemeProvider
const AppWithTheme: React.FC<{ children: ReactNode }> = ({ children }) => {
  const setThemeCookie = (mode: 'dark' | 'light') => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const host = window.location.hostname;
    const isBitBotHubDomain = host.endsWith('bitbothub.com');
    const parts = [
      `theme-mode=${mode}`,
      'Path=/',
      'Max-Age=31536000',
      'SameSite=Lax',
    ];
    if (isBitBotHubDomain) {
      parts.push('Domain=.bitbothub.com');
    }
    if (isBitBotHubDomain || window.location.protocol === 'https:') {
      parts.push('Secure');
    }
    document.cookie = parts.join('; ');
  };

  const getThemeFromCookie = (): 'dark' | 'light' | null => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )theme-mode=([^;]+)/i);
    const value = match ? decodeURIComponent(match[1]) : null;
    return value === 'dark' || value === 'light' ? value : null;
  };

  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const saved = getThemeFromCookie() ?? localStorage.getItem('theme-mode');
    if (saved === 'dark' || saved === 'light') {
      return saved === 'dark';
    }
    return true;
  });

  const toggleTheme = () => {
    setIsDark((prev) => {
      const newValue = !prev;
      const nextMode = newValue ? 'dark' : 'light';
      localStorage.setItem('theme-mode', nextMode);
      setThemeCookie(nextMode);
      return newValue;
    });
  };

  useEffect(() => {
    setThemeCookie(isDark ? 'dark' : 'light');
  }, [isDark]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
          ...(isDark
            ? {
                background: {
                  default: '#0A0F1A',
                  paper: '#1A202C',
                },
                primary: {
                  main: '#4C7AE5',
                },
              }
            : {
                background: {
                  default: '#f3f6fb',
                  paper: '#ffffff',
                },
                primary: {
                  main: '#1976d2',
                },
              }),
        },
      }),
    [isDark]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeContext.Provider value={{ isDark, toggleTheme }}>
        {children}
      </ThemeContext.Provider>
    </ThemeProvider>
  );
};

const formatAutopilotDateTime = (
  value: string | Date | null | undefined,
  locale?: string
): string => {
  if (!value) {
    return '—';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatAutopilotMoney = (value: number | null | undefined, digits = 2): string => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '—';
  }

  return `${numeric >= 0 ? '+' : ''}${numeric.toFixed(digits)}$`;
};

function App() {
  const dispatch = useAppDispatch();
  const token = useAppSelector(state => state.auth.token);
  const bots = useAppSelector(state => state.bots.bots);
  const { t, i18n } = useTranslation();
  const [tutorialActive, setTutorialActive] = useState(() => {
    if (typeof window === 'undefined') return false;
    return Boolean((window as any).__BITBOT_TUTORIAL_ACTIVE__);
  });
  const [autopilotBootstrap, setAutopilotBootstrap] =
    useState<AutopilotBootstrapResponse | null>(null);
  const [autopilotReport, setAutopilotReport] =
    useState<AutopilotSessionReportResponse | null>(null);
  const [autopilotWidgetExpanded, setAutopilotWidgetExpanded] = useState(false);
  const [autopilotBootstrapLoading, setAutopilotBootstrapLoading] = useState(false);
  const [autopilotReportLoading, setAutopilotReportLoading] = useState(false);
  const autopilotWidgetRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const role = useRoleFromToken(token);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isAdmin = role === 'admin';
  const { status: maintenanceStatus } = useMaintenanceStatus();
  const maintenanceGate = Boolean(maintenanceStatus.enabled) && !isAdmin;
  const navigate = useNavigate();
  const location = useLocation();
  const seoLang = useMemo(() => {
    const normalized = String(i18n.resolvedLanguage || i18n.language || 'en')
      .trim()
      .toLowerCase();

    if (normalized === 'ru') return 'ru';
    if (normalized === 'ua' || normalized === 'uk') return 'uk';
    return 'en';
  }, [i18n.language, i18n.resolvedLanguage]);

  // ---- isDesktop: десктоп, если ширина >= 992px и не мобильный userAgent
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const ua = navigator.userAgent || '';
    const isMobileUA = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua);
    const mq = window.matchMedia('(min-width: 992px)');
    return mq.matches && !isMobileUA;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ua = navigator.userAgent || '';
    const isMobileUA = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua);
    const mq = window.matchMedia('(min-width: 992px)');

    const handleChange = () => setIsDesktop(mq.matches && !isMobileUA);

    // начальная синхронизация (на случай смены UA не рассчитываем)
    handleChange();

    // подписка на изменения размера
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
    } else {
      // Safari < 14
      mq.addListener(handleChange as any);
      return () => mq.removeListener(handleChange as any);
    }
  }, []);

  useEffect(() => {
    const handleLogout = () => {
      dispatch(logout());
      navigate('/login', { replace: true });
    };

    const handleAuthTokensUpdated = (
      event: Event
    ) => {
      const detail = (event as CustomEvent<{
        token?: string;
        refreshToken?: string;
      }>).detail;

      if (!detail?.token || !detail?.refreshToken) {
        return;
      }

      dispatch(
        setAuthSession({
          token: detail.token,
          refreshToken: detail.refreshToken,
        })
      );
    };

    window.addEventListener('logout', handleLogout);
    window.addEventListener(
      'auth:tokens-updated',
      handleAuthTokensUpdated as EventListener
    );
    return () => {
      window.removeEventListener('logout', handleLogout);
      window.removeEventListener(
        'auth:tokens-updated',
        handleAuthTokensUpdated as EventListener
      );
    };
  }, [dispatch, navigate]);

  // Валидируем токен при старте, но отложено на 500ms чтобы не конфликтовать с логином
  useEffect(() => {
    if (!token) return; // Не валидируем если нет токена
    
    const timer = setTimeout(() => {
      dispatch(validateToken());
    }, 500);
    
    return () => clearTimeout(timer);
  }, [dispatch, token]);

  // Проверяем, находимся ли на /login
  const isAuthPage = ['/login', '/register', '/auth/callback', '/privacy', '/policy', '/signals'].includes(location.pathname);
  const isPublicLayout = !token || maintenanceGate || isAuthPage;
  const muiTheme = useTheme();
  const errorBotCount = useMemo(
    () => bots.filter((bot) => Boolean(bot.hasUnreadExchangeError)).length,
    [bots]
  );
  const visibleErrorFlagCount = errorBotCount > 0 ? errorBotCount : tutorialActive ? 1 : 0;
  const showRootFlags = Boolean(token && !maintenanceGate && !isAuthPage && location.pathname === '/');
  const shouldLoadAutopilotWidget = Boolean(showRootFlags);
  const activeAutopilotSession = autopilotBootstrap?.activeSession ?? null;
  const activeAutopilotSessionId = activeAutopilotSession?._id || '';
  const activeAutopilotReport =
    activeAutopilotSessionId && autopilotReport?.session?._id === activeAutopilotSessionId
      ? autopilotReport
      : null;
  const autopilotManagedBotsForSession = useMemo(
    () =>
      activeAutopilotSessionId
        ? bots.filter(
            (bot) =>
              bot.managedBy === 'autopilot' &&
              bot.autopilotSessionId === activeAutopilotSessionId
          )
        : [],
    [activeAutopilotSessionId, bots]
  );
  const autopilotRunningBotsCount = useMemo(
    () => autopilotManagedBotsForSession.filter((bot) => bot.status === 'running').length,
    [autopilotManagedBotsForSession]
  );
  const visibleAutopilotFlag = Boolean(showRootFlags && activeAutopilotSession);
  const autopilotNetPnl = activeAutopilotReport?.report.summary.trades.totalNetPnl ?? null;
  const autopilotLast24hNetPnl =
    activeAutopilotReport?.report.summary.trades.last24hNetPnl ?? null;
  const autopilotWinRate = activeAutopilotReport?.report.summary.trades.winRate ?? null;
  const autopilotClosedTrades = activeAutopilotReport?.report.summary.trades.closedTrades ?? null;
  const autopilotLastActivity =
    activeAutopilotReport?.report.summary.actions.lastActionAt ||
    activeAutopilotSession?.lastDecisionAt ||
    activeAutopilotSession?.lastHeartbeatAt ||
    null;
  const autopilotPnlColor =
    typeof autopilotNetPnl === 'number' && Number.isFinite(autopilotNetPnl)
      ? autopilotNetPnl > 0
        ? '#43a047'
        : autopilotNetPnl < 0
          ? '#d32f2f'
          : muiTheme.palette.text.primary
      : muiTheme.palette.text.primary;
  const autopilotLast24hPnlColor =
    typeof autopilotLast24hNetPnl === 'number' && Number.isFinite(autopilotLast24hNetPnl)
      ? autopilotLast24hNetPnl > 0
        ? '#43a047'
        : autopilotLast24hNetPnl < 0
          ? '#d32f2f'
          : muiTheme.palette.text.primary
      : muiTheme.palette.text.primary;

  const refreshAutopilotBootstrap = useCallback(
    async (silent = false) => {
      if (!shouldLoadAutopilotWidget) {
        setAutopilotBootstrap(null);
        return;
      }

      if (!silent) {
        setAutopilotBootstrapLoading(true);
      }

      try {
        const response = await axios.get<AutopilotBootstrapResponse>('/autopilot/bootstrap');
        setAutopilotBootstrap(response.data);
      } catch {
        setAutopilotBootstrap(null);
      } finally {
        if (!silent) {
          setAutopilotBootstrapLoading(false);
        }
      }
    },
    [shouldLoadAutopilotWidget]
  );

  const loadAutopilotReport = useCallback(async (sessionId: string, silent = false) => {
    if (!sessionId) {
      setAutopilotReport(null);
      return;
    }

    if (!silent) {
      setAutopilotReportLoading(true);
    }

    try {
      const response = await axios.get<AutopilotSessionReportResponse>(
        `/autopilot/sessions/${sessionId}`
      );
      setAutopilotReport(response.data);
    } catch {
      setAutopilotReport(null);
    } finally {
      if (!silent) {
        setAutopilotReportLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onTutorialActive = (event: Event) => {
      const detail = (event as CustomEvent<{ active?: boolean }>).detail;
      setTutorialActive(Boolean(detail?.active));
    };

    window.addEventListener('bitbot:tutorial:active', onTutorialActive as EventListener);
    return () => window.removeEventListener('bitbot:tutorial:active', onTutorialActive as EventListener);
  }, []);

  useEffect(() => {
    if (!token) {
      setAutopilotBootstrap(null);
      setAutopilotReport(null);
      setAutopilotWidgetExpanded(false);
      return;
    }
  }, [token]);

  useEffect(() => {
    if (!shouldLoadAutopilotWidget) {
      setAutopilotBootstrap(null);
      setAutopilotReport(null);
      setAutopilotWidgetExpanded(false);
      return;
    }

    void refreshAutopilotBootstrap();
    const intervalId = window.setInterval(() => {
      void refreshAutopilotBootstrap(true);
    }, 45000);

    return () => window.clearInterval(intervalId);
  }, [refreshAutopilotBootstrap, shouldLoadAutopilotWidget]);

  useEffect(() => {
    if (!activeAutopilotSessionId) {
      setAutopilotWidgetExpanded(false);
      setAutopilotReport(null);
      return;
    }

    const loadCurrentReport = (silent = true) =>
      loadAutopilotReport(activeAutopilotSessionId, silent);

    void loadCurrentReport(!autopilotWidgetExpanded);

    const handleWindowFocus = () => {
      void loadCurrentReport(true);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void loadCurrentReport(true);
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadCurrentReport(true);
      }
    }, 30000);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeAutopilotSessionId, autopilotWidgetExpanded, loadAutopilotReport]);

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldLoadAutopilotWidget) {
      return;
    }

    const handleAutopilotUpdated = () => {
      void refreshAutopilotBootstrap(true);
      if (activeAutopilotSessionId) {
        void loadAutopilotReport(activeAutopilotSessionId, true);
      }
    };

    window.addEventListener(
      'bitbot:autopilot:updated',
      handleAutopilotUpdated as EventListener
    );

    return () =>
      window.removeEventListener(
        'bitbot:autopilot:updated',
        handleAutopilotUpdated as EventListener
      );
  }, [
    activeAutopilotSessionId,
    loadAutopilotReport,
    refreshAutopilotBootstrap,
    shouldLoadAutopilotWidget,
  ]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !autopilotWidgetExpanded ||
      !visibleAutopilotFlag
    ) {
      return;
    }

    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      const widgetElement = autopilotWidgetRef.current;
      const targetNode = event.target as Node | null;

      if (!widgetElement || !targetNode || widgetElement.contains(targetNode)) {
        return;
      }

      setAutopilotWidgetExpanded(false);
    };

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, [autopilotWidgetExpanded, visibleAutopilotFlag]);

  const handleAutopilotWidgetToggle = useCallback(() => {
    if (!visibleAutopilotFlag) {
      return;
    }

    setAutopilotWidgetExpanded((current) => !current);
  }, [visibleAutopilotFlag]);

  const scrollToFirstErrorBot = useCallback(() => {
    if (typeof document === 'undefined') return false;

    const firstErrorBot = document.querySelector<HTMLElement>('[data-bot-error="true"]');
    if (!firstErrorBot) return false;

    firstErrorBot.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  const handleErrorFlagClick = useCallback(() => {
    if (visibleErrorFlagCount <= 0) return;

    if (location.pathname !== '/') {
      navigate('/#bot-errors');
      return;
    }

    if (!scrollToFirstErrorBot()) {
      window.setTimeout(() => {
        scrollToFirstErrorBot();
      }, 120);
    }
  }, [location.pathname, navigate, scrollToFirstErrorBot, visibleErrorFlagCount]);

  useEffect(() => {
    const isLandingRoute = location.pathname === '/' && (!token || maintenanceGate);
    const isPublicSignalsRoute = location.pathname === '/signals';
    if (isLandingRoute || isPublicSignalsRoute) {
      return;
    }

    const routeTitle =
      location.pathname === '/login'
        ? `${t('auth.login')} | BitBotHub`
        : location.pathname === '/register'
          ? `${t('auth.createAccount')} | BitBotHub`
          : location.pathname === '/policy' || location.pathname === '/privacy'
            ? `${t('auth.privacyPolicy')} | BitBotHub`
            : location.pathname === '/payments'
              ? `${t('payments.title')} | BitBotHub`
              : location.pathname === '/admin/payments'
                ? `${t('payments.adminTitle')} | BitBotHub`
                : location.pathname === '/custom-strategies'
                  ? `${t('customStrategies.title', { defaultValue: 'Custom Strategies' })} | BitBotHub`
                : 'BitBotHub Dashboard';

    applySeo({
      title: routeTitle,
      description: t('landing.seoDescription'),
      robots: 'noindex, nofollow',
      canonical: null,
      lang: seoLang,
      author: 'BitBotHub',
      applicationName: 'BitBotHub',
      openGraph: null,
      twitter: null,
      structuredData: null,
    });
  }, [location.pathname, maintenanceGate, seoLang, t, token]);

  useEffect(() => {
    if (location.pathname !== '/' || location.hash !== '#bot-errors' || visibleErrorFlagCount <= 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      if (scrollToFirstErrorBot()) {
        navigate({ pathname: '/', search: location.search }, { replace: true });
      }
    }, 120);

    return () => window.clearTimeout(timerId);
  }, [location.hash, location.pathname, location.search, navigate, scrollToFirstErrorBot, visibleErrorFlagCount]);

  const withRouteSuspense = (element: ReactNode) => (
    <Suspense fallback={<Box sx={{ minHeight: 120 }} />}>
      {element}
    </Suspense>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: muiTheme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: isPublicLayout ? '0' : { xs: '16px 2px', sm: '32px 8px' },
        transition: 'background-color 0.3s ease',
        '--border-color': muiTheme.palette.mode === 'dark' ? '#2d3748' : '#eef0f4',
        '--header-bg': muiTheme.palette.mode === 'dark' ? '#2d3748' : '#f0f3f8',
        '--status-running': muiTheme.palette.mode === 'dark' ? '#4caf50' : '#27ae60',
        '--status-stopped': muiTheme.palette.mode === 'dark' ? '#90a4ae' : '#8395a7',
        '--status-pending': muiTheme.palette.mode === 'dark' ? '#ffa726' : '#f39c12',
        '--stat-sl-bg': muiTheme.palette.mode === 'dark' ? 'rgba(255, 0, 0, 0.25)' : 'rgba(255, 0, 0, 0.15)',
        '--stat-tp-bg': muiTheme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.25)' : 'rgba(39, 174, 96, 0.2)',
        '--stat-green-bg': muiTheme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.25)' : 'rgba(39, 174, 96, 0.2)',
        '--stat-red-bg': muiTheme.palette.mode === 'dark' ? 'rgba(255, 0, 0, 0.25)' : 'rgba(255, 0, 0, 0.15)',
        '--stat-error-bg': muiTheme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.25)' : 'rgba(255, 87, 34, 0.14)',
        '--account-text': muiTheme.palette.mode === 'dark' ? '#ddd' : '#223',
      } as any}
    >
      <ContentWrapper isLoginPage={isPublicLayout} isDesktop={isDesktop}>
        <Routes>
          <Route
            path="/login"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                <Navigate to="/" replace />
              ) : (
                withRouteSuspense(<LoginForm />)
              )
            }
          />

          <Route
            path="/register"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                <Navigate to="/" replace />
              ) : (
                withRouteSuspense(<RegisterPage />)
              )
            }
          />

          <Route
            path="/auth/callback"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                <Navigate to="/" replace />
              ) : (
                withRouteSuspense(<OAuthCallbackPage />)
              )
            }
          />

          <Route
            path="/policy"
            element={withRouteSuspense(<PrivacyPage />)}
          />

          <Route
            path="/privacy"
            element={<Navigate to="/policy" replace />}
          />

          <Route
            path="/signals"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : (
                withRouteSuspense(<PublicSignalsPage />)
              )
            }
          />

          <Route
            path="/"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                withRouteSuspense(
                  <>
                    <NotificationBanner />
                    <Navbar />
                    <BotList />
                    <BotForm />
                    <BinanceAccountForm />
                    <GlobalStat />
                  </>
                )
              ) : (
                <LandingPage maintenance={maintenanceStatus} />
              )
            }
          />

          <Route
            path="/payments"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                withRouteSuspense(
                  <>
                    <NotificationBanner />
                    <Navbar />
                    <UserPaymentDashboard />
                  </>
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/admin/payments"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token && isAdmin ? (
                withRouteSuspense(
                  <>
                    <Navbar />
                    <AdminPaymentDashboard />
                  </>
                )
              ) : token ? (
                <Navigate to="/" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/custom-strategies"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                withRouteSuspense(
                  <>
                    <NotificationBanner />
                    <Navbar />
                    <CustomStrategiesPage />
                  </>
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/analytics"
            element={
              maintenanceGate ? (
                <LandingPage maintenance={maintenanceStatus} />
              ) : token ? (
                withRouteSuspense(
                  <>
                    <NotificationBanner />
                    <Navbar />
                    <AnalyticsPage />
                  </>
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ContentWrapper>

      {token && !maintenanceGate && !isAuthPage ? (
        <Box
          sx={{
            position: 'fixed',
            top: 'calc(-6px + 5vh)',
            right: 0,
            zIndex: 1250,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.75,
          }}
        >
          <Box
            component="a"
            href="https://news.bitbothub.com"
            aria-label="Open news"
            data-tour="app-news-portal"
            sx={{
              width: { xs: 42, sm: 42 },
              height: { xs: 44, sm: 44 },
              borderRadius: '14px 0 0 14px',
              border: '1px solid rgba(13, 71, 161, 0.20)',
              backgroundColor: '#ffffff',
              boxShadow: '0 12px 28px rgba(13, 71, 161, 0.16)',
              color: '#0d47a1',
              textDecoration: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.18s ease, background-color 0.18s ease',
              '&:hover': {
                transform: 'translateX(-2px)',
              },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                border: '3px solid #0d47a1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0d47a1',
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              N
            </Box>
          </Box>
          {visibleAutopilotFlag ? (
            <Box
              ref={autopilotWidgetRef}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}
            >
              <Box
                component="button"
                type="button"
                onClick={handleAutopilotWidgetToggle}
                aria-label={t('autopilot.flag.toggle', {
                  defaultValue: 'Toggle Autopilot status',
                })}
                aria-expanded={autopilotWidgetExpanded}
                sx={{
                  position: 'relative',
                  width: { xs: 42, sm: 42 },
                  height: { xs: 44, sm: 44 },
                  borderRadius: '14px 0 0 14px',
                  border: '1px solid rgba(5, 150, 105, 0.24)',
                  backgroundColor:
                    muiTheme.palette.mode === 'dark' ? 'rgba(6, 78, 59, 0.92)' : '#ecfdf5',
                  boxShadow:
                    muiTheme.palette.mode === 'dark'
                      ? '0 12px 28px rgba(5, 150, 105, 0.18)'
                      : '0 12px 28px rgba(5, 150, 105, 0.16)',
                  color: muiTheme.palette.mode === 'dark' ? '#6ee7b7' : '#047857',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.18s ease, background-color 0.18s ease',
                  '&:hover': {
                    transform: 'translateX(-2px)',
                  },
                }}
              >
                {autopilotBootstrapLoading ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                ) : (
                  <RocketLaunchOutlinedIcon sx={{ fontSize: 20 }} />
                )}
                {autopilotRunningBotsCount > 0 ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 6,
                      minWidth: 16,
                      height: 16,
                      px: 0.25,
                      borderRadius: '999px',
                      backgroundColor: '#ffffff',
                      color: '#047857',
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {autopilotRunningBotsCount}
                  </Box>
                ) : null}
              </Box>
              {autopilotWidgetExpanded ? (
                <Box
                  sx={{
                    width: { xs: 264, sm: 296 },
                    p: 1.5,
                    borderRadius: '18px 0 0 18px',
                    border: '1px solid',
                    borderColor:
                      muiTheme.palette.mode === 'dark'
                        ? 'rgba(96, 165, 250, 0.22)'
                        : 'rgba(15, 23, 42, 0.08)',
                    bgcolor: muiTheme.palette.background.paper,
                    boxShadow:
                      muiTheme.palette.mode === 'dark'
                        ? '0 18px 44px rgba(0, 0, 0, 0.42)'
                        : '0 18px 44px rgba(15, 23, 42, 0.14)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1.1,
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flexWrap: 'wrap',
                        }}
                      >
                        <RocketLaunchOutlinedIcon
                          sx={{
                            fontSize: 18,
                            color: muiTheme.palette.mode === 'dark' ? '#6ee7b7' : '#047857',
                          }}
                        />
                        <Box
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.92rem',
                            lineHeight: 1.2,
                          }}
                        >
                          {t('autopilot.flag.title', {
                            defaultValue: 'Autopilot is running',
                          })}
                        </Box>
                        <AutopilotBetaBadge compact />
                      </Box>
                      <Box
                        sx={{
                          mt: 0.45,
                          color: 'text.secondary',
                          fontSize: '0.73rem',
                          lineHeight: 1.35,
                        }}
                      >
                        {autopilotBootstrap?.profile?.name ||
                          t('autopilot.title', { defaultValue: 'Autopilot' })}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        px: 0.9,
                        py: 0.45,
                        borderRadius: '999px',
                        bgcolor:
                          activeAutopilotSession?.status === 'running'
                            ? 'rgba(67, 160, 71, 0.16)'
                            : activeAutopilotSession?.status === 'stopping'
                              ? 'rgba(251, 140, 0, 0.18)'
                              : 'rgba(100, 116, 139, 0.18)',
                        color:
                          activeAutopilotSession?.status === 'running'
                            ? '#2e7d32'
                            : activeAutopilotSession?.status === 'stopping'
                              ? '#ef6c00'
                              : muiTheme.palette.text.secondary,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t(`autopilot.status.${activeAutopilotSession?.status || 'running'}`, {
                        defaultValue: activeAutopilotSession?.status || 'running',
                      })}
                    </Box>
                  </Box>

                  {autopilotReportLoading && !activeAutopilotReport ? (
                    <Box
                      sx={{
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress size={18} thickness={5} />
                    </Box>
                  ) : (
                    <Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          gap: 0.85,
                        }}
                      >
                        {[
                          {
                            label: t('autopilot.flag.runningBots', {
                              defaultValue: 'Running bots',
                            }),
                            value: `${autopilotRunningBotsCount}/${activeAutopilotSession?.settingsSnapshot.maxActiveBots ?? 0}`,
                            color: 'text.primary',
                          },
                          {
                            label: t('autopilot.flag.mode', { defaultValue: 'Mode' }),
                            value: t(`autopilot.mode.${activeAutopilotSession?.mode || 'simulation'}`, {
                              defaultValue: activeAutopilotSession?.mode || 'simulation',
                            }),
                            color: 'text.primary',
                          },
                          {
                            label: t('autopilot.flag.netPnl', { defaultValue: 'Net PnL' }),
                            value: formatAutopilotMoney(autopilotNetPnl),
                            color: autopilotPnlColor,
                          },
                          {
                            label: t('autopilot.flag.winRate', { defaultValue: 'Win rate' }),
                            value:
                              typeof autopilotWinRate === 'number' && Number.isFinite(autopilotWinRate)
                                ? `${autopilotWinRate.toFixed(1)}%`
                                : '—',
                            color: 'text.primary',
                          },
                        ].map((item) => (
                          <Box
                            key={item.label}
                            sx={{
                              p: 1,
                              borderRadius: 2.5,
                              bgcolor:
                                muiTheme.palette.mode === 'dark'
                                  ? 'rgba(15, 23, 42, 0.54)'
                                  : 'rgba(248, 250, 252, 0.94)',
                              border: '1px solid',
                              borderColor:
                                muiTheme.palette.mode === 'dark'
                                  ? 'rgba(148, 163, 184, 0.16)'
                                  : 'rgba(148, 163, 184, 0.18)',
                            }}
                          >
                            <Box
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.68rem',
                                lineHeight: 1.25,
                              }}
                            >
                              {item.label}
                            </Box>
                            <Box
                              sx={{
                                mt: 0.35,
                                color: item.color,
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {item.value}
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          mt: 1,
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto',
                          rowGap: 0.6,
                          columnGap: 1,
                          alignItems: 'start',
                        }}
                      >
                        <Box sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t('autopilot.flag.entryAmount', {
                            defaultValue: 'Entry amount',
                          })}
                        </Box>
                        <Box sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                          {formatAutopilotMoney(
                            activeAutopilotSession?.settingsSnapshot.entryAmount ?? null,
                            0
                          )}
                        </Box>
                        <Box sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t('autopilot.flag.totalBudget', {
                            defaultValue: 'Budget',
                          })}
                        </Box>
                        <Box sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                          {formatAutopilotMoney(
                            activeAutopilotSession?.settingsSnapshot.totalBudget ?? null,
                            0
                          )}
                        </Box>
                        <Box sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t('autopilot.flag.last24hPnl', {
                            defaultValue: '24h session PnL',
                          })}
                        </Box>
                        <Box
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: autopilotLast24hPnlColor,
                          }}
                        >
                          {formatAutopilotMoney(autopilotLast24hNetPnl)}
                        </Box>
                        <Box sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t('autopilot.flag.closedTrades', {
                            defaultValue: 'Closed trades',
                          })}
                        </Box>
                        <Box sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                          {typeof autopilotClosedTrades === 'number' ? autopilotClosedTrades : '—'}
                        </Box>
                        <Box sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                          {t('autopilot.flag.lastCycle', {
                            defaultValue: 'Last cycle',
                          })}
                        </Box>
                        <Box
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            textAlign: 'right',
                            maxWidth: 156,
                          }}
                        >
                          {formatAutopilotDateTime(
                            autopilotLastActivity,
                            i18n.resolvedLanguage || i18n.language
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : null}
            </Box>
          ) : null}
          {visibleErrorFlagCount > 0 ? (
            <Box
              component="button"
              type="button"
              onClick={handleErrorFlagClick}
              aria-label={`Show ${visibleErrorFlagCount} bots with exchange errors`}
              data-tour="app-error-flag"
              sx={{
                minWidth: { xs: 24, sm: 24 },
                height: { xs: 24, sm: 24 },
                px: 0.5,
                borderRadius: '10px 0 0 10px',
                border: '1px solid rgba(211, 47, 47, 0.28)',
                backgroundColor: '#fff6f4',
                boxShadow: '0 8px 18px rgba(211, 47, 47, 0.18)',
                color: '#d32f2f',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: '0.72rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {visibleErrorFlagCount}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default function AppWrapper() {
  return (
    <AppWithTheme>
      <App />
    </AppWithTheme>
  );
}
